import { api } from '@/utils/Api/POS';
// @ts-ignore
import { GetServiceGroup } from '@/utils/Api/Service';
import {
    BookingDiscount,
    BookingDiscountsListParams,
    BookingDiscountsListResponse,
    CreateBookingDiscountPayload,
    DeleteBookingDiscountResponse,
    GenerateDiscountCodeResponse,
    TreatmentGroup,
    TreatmentItem,
    UpdateBookingDiscountPayload,
} from '../Types/booking-discounts.types';

const mapServiceToTreatmentItem = (serviceItem: any, groupId: number): TreatmentItem => ({
    id: Number(serviceItem?.id),
    name: serviceItem?.name || '',
    price: Number(serviceItem?.price || 0),
    groupId,
    sequence: serviceItem?.sequence ?? null,
});

/** Same grouping DSA as Services index `transformServiceList`. */
const transformServiceGroups = (data: any[]): TreatmentGroup[] => {
    const groupedServices: TreatmentGroup[] = data
        .filter((group) => group.id !== 0 && group.id !== null)
        .map((group) => {
            const groupId = Number(group.id);
            const services = Array.isArray(group.services)
                ? group.services.map((serviceItem: any) => mapServiceToTreatmentItem(serviceItem, groupId))
                : [];

            if (services.some((serviceItem: TreatmentItem) => serviceItem.sequence === null)) {
                services.sort((a: TreatmentItem, b: TreatmentItem) => a.id - b.id);
            } else {
                services.sort((a: TreatmentItem, b: TreatmentItem) => (a.sequence ?? 0) - (b.sequence ?? 0));
            }

            return {
                title: group.group || '',
                groupId,
                sequence: Number(group.sequence ?? groupId),
                services,
            };
        });

    const ungroupedServices = data
        .filter((group) => group.id == 0 || group.id == null)
        .flatMap((group) => (Array.isArray(group.services) ? group.services : []))
        .map((serviceItem: any) => mapServiceToTreatmentItem(serviceItem, 0));

    if (ungroupedServices.some((serviceItem) => serviceItem.sequence === null)) {
        ungroupedServices.sort((a, b) => a.id - b.id);
    } else {
        ungroupedServices.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
    }

    const serviceList: TreatmentGroup[] = [
        {
            title: '',
            groupId: 0,
            sequence: 0,
            services: ungroupedServices,
        },
        ...groupedServices,
    ].sort((a, b) => a.sequence - b.sequence);

    return serviceList.filter((group) => group.groupId !== 0 || group.services.length > 0);
};

export const bookingDiscountsApi = {
    async listBookingDiscounts(params?: BookingDiscountsListParams): Promise<BookingDiscountsListResponse> {
        const response: any = await api.getApiListBookingDiscounts(params);
        const rawData = response?.data || response || {};
        return {
            currentPage: Number(rawData.currentPage) || 1,
            totalPages: Number(rawData.totalPages) || 0,
            limit: Number(rawData.limit) || params?.limit || 10,
            total: Number(rawData.total) || 0,
            discounts: Array.isArray(rawData.discounts) ? rawData.discounts : [],
        };
    },

    async getBookingDiscountById(discountId: string): Promise<BookingDiscount> {
        const response: any = await api.getApiBookingDiscountId(discountId);
        return response?.data || response;
    },

    async createBookingDiscount(payload: CreateBookingDiscountPayload): Promise<BookingDiscount> {
        const response: any = await api.postApiBookingDiscount(payload as any);
        return response?.data || response;
    },

    async updateBookingDiscount(discountId: string, payload: UpdateBookingDiscountPayload): Promise<BookingDiscount> {
        const response: any = await api.putApiBookingDiscountId(discountId, payload as any);
        return response?.data || response;
    },

    async deleteBookingDiscount(discountId: string): Promise<DeleteBookingDiscountResponse> {
        const response: any = await api.deleteApiBookingDiscountId(discountId);
        return response;
    },

    async generateDiscountCode(): Promise<GenerateDiscountCodeResponse> {
        const response: any = await api.getApiGenerateDiscountCode();
        return response?.data || response || { code: '' };
    },

    async fetchTreatmentsList(): Promise<TreatmentGroup[]> {
        try {
            const response = await GetServiceGroup();
            const groupList = response?.data?.data || response?.data || [];
            if (!Array.isArray(groupList)) {
                return [];
            }
            return transformServiceGroups(groupList);
        } catch (errorItem) {
            console.error('Error fetching treatment list for booking discounts:', errorItem);
            return [];
        }
    },
};
